import React, { useState, useEffect } from "react";
import _ from "lodash";
import * as yup from "yup";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { t } from "../../../../utils/translate";
import { MdDeleteOutline } from "react-icons/md";
import { MdOutlineEditNote } from "react-icons/md";
import { LiaFileUploadSolid } from "react-icons/lia";
import { yupResolver } from "@hookform/resolvers/yup";
import CloseIcon from "../../../../Assets/iconclose.svg";
import { Link, useNavigate, useParams } from "react-router-dom";
import ImageUpload from "../../../../components/FileUpload";
import SignatureModal from "../../../../components/SignatureModal";
import { english, norway } from "../../../../components/forms/data";
import { createFilesService } from "../../../../Services/filesServices";
import {
  getProjectByIdService,
  getProjectByUserId,
} from "../../../../Services/projectService";
import { getPriceFormByProjectIdService } from "../../../../Services/priceFormService";
import {
  approvalFormCreateService,
  getApprovalFormByProjectId,
} from "../../../../Services/approvalFormService";
import MainLayout from "../../../components/layout/MainLayout";
import { Plus } from "lucide-react";

// PDF Generator Import
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const schema = yup.object().shape({
  workOrderNumber: yup
    .string()
    .required(t("scaffoldIdentification/NumberIsRequired")),
  date: yup.string().required(t("dateIsRequired")),
  dismantledDate: yup.string().optional(),
  location: yup.string().required(t("locationIsRequired")),
  scaffolderowner: yup.string(),
  inspectedBy: yup.string(),
  builtBy: yup.string(),
  userResponsible: yup.string(),
  scaffoldClass: yup.string(),
  AnchorCapacityUnit: yup.string(),
  totalWeightPerM2: yup.string().typeError(t("totalWeightPerM2MustBeNumber")),
  amountWallAnkers: yup.string().typeError(t("amountOfWallAnkersMustBeNumber")),
});

const styles = {
  color: "red",
};

const left = {
  left: "132px",
};

// PDF Generator Function
const generateApprovalFormPDF = (formData, approvalForm, scaffoldName, visual, userGuideDetail, scaffoldStatus) => {
  const doc = new jsPDF('p', 'mm', 'a4');
  let yPosition = 20;
  const pageWidth = doc.internal.pageSize.width;
  const margin = 20;
  const contentWidth = pageWidth - (margin * 2);

  // Helper function to add a new page if needed
  const checkPageBreak = (requiredSpace = 10) => {
    if (yPosition + requiredSpace > 280) {
      doc.addPage();
      yPosition = 20;
      return true;
    }
    return false;
  };

  // Helper function to add text with word wrap
  const addWrappedText = (text, x, y, maxWidth, fontSize = 10) => {
    doc.setFontSize(fontSize);
    const lines = doc.splitTextToSize(text, maxWidth);
    doc.text(lines, x, y);
    return lines.length * (fontSize * 0.35);
  };

  // Header Section
  const addHeader = () => {
    // Company Logo Area (if backgroundImage exists)
    if (formData.backgroundImage) {
      try {
        doc.addImage(formData.backgroundImage, 'JPEG', margin, 10, 40, 20);
      } catch (error) {
        console.log('Could not add background image');
      }
    }

    // Title
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 114, 187); // Blue color
    doc.text('SCAFFOLD APPROVAL FORM', pageWidth / 2, yPosition, { align: 'center' });
    
    yPosition += 15;

    // Form Information Box
    doc.setFillColor(240, 248, 255);
    doc.rect(margin, yPosition, contentWidth, 25, 'F');
    doc.setDrawColor(0, 114, 187);
    doc.rect(margin, yPosition, contentWidth, 25);

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    
    // Form details in two columns
    doc.text(`Scaffold ID: ${formData.scaffoldId || 'N/A'}`, margin + 5, yPosition + 8);
    doc.text(`Project Number: ${formData.projectNumber || 'N/A'}`, pageWidth / 2, yPosition + 8);
    doc.text(`Status: ${scaffoldStatus?.toUpperCase() || 'INACTIVE'}`, margin + 5, yPosition + 16);
    doc.text(`Date Created: ${new Date().toLocaleDateString()}`, pageWidth / 2, yPosition + 16);

    yPosition += 35;
  };

  // Section Header Helper
  const addSectionHeader = (title, backgroundColor = [0, 114, 187]) => {
    checkPageBreak(15);
    
    doc.setFillColor(...backgroundColor);
    doc.rect(margin, yPosition, contentWidth, 8, 'F');
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text(title, margin + 5, yPosition + 6);
    
    yPosition += 15;
    doc.setTextColor(0, 0, 0);
  };

  // Basic Information Section
  const addBasicInformation = () => {
    addSectionHeader('BASIC INFORMATION');

    const basicFields = [
      { label: 'Work Order Number', value: formData.workOrderNumber },
      { label: 'Location', value: formData.location },
      { label: 'Build Date', value: formData.date },
      { label: 'Dismantle Date', value: formData.dismantledDate }
    ];

    basicFields.forEach((field, index) => {
      if (index % 2 === 0) checkPageBreak();
      
      const xPos = (index % 2 === 0) ? margin : pageWidth / 2;
      
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text(`${field.label}:`, xPos, yPosition);
      
      doc.setFont('helvetica', 'normal');
      doc.text(field.value || 'N/A', xPos, yPosition + 5);
      
      if (index % 2 === 1) yPosition += 15;
    });

    if (basicFields.length % 2 === 1) yPosition += 15;
  };

  // Scaffold Names Section
  const addScaffoldNames = () => {
    if (!scaffoldName || scaffoldName.length === 0) return;

    addSectionHeader('SCAFFOLD DETAILS');

    scaffoldName.forEach((scaffold, index) => {
      checkPageBreak(10);
      
      doc.setFillColor(245, 245, 245);
      doc.rect(margin, yPosition, contentWidth, 8, 'F');
      
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text(`${index + 1}. ${scaffold.value} (${scaffold.key})`, margin + 3, yPosition + 5);
      
      yPosition += 12;

      // Add measurements if they exist
      if (scaffold.measurements) {
        Object.entries(scaffold.measurements).forEach(([unit, measurements]) => {
          if (measurements && measurements.length > 0) {
            checkPageBreak();
            
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.text(`${unit.toUpperCase()}:`, margin + 10, yPosition);
            
            measurements.forEach((measurement, idx) => {
              let measurementText = '';
              if (measurement.length && measurement.width && measurement.height) {
                measurementText = `${measurement.length} × ${measurement.width} × ${measurement.height}`;
              } else if (measurement.length && measurement.width) {
                measurementText = `${measurement.length} × ${measurement.width}`;
              } else if (measurement.length) {
                measurementText = measurement.length;
              } else if (measurement.height) {
                measurementText = measurement.height;
              } else if (measurement.value) {
                measurementText = measurement.value;
              }
              
              if (measurementText) {
                doc.text(`  • ${measurementText}`, margin + 15, yPosition + 5 + (idx * 4));
              }
            });
            
            yPosition += Math.max(measurements.length * 4, 8);
          }
        });
      }
      
      yPosition += 5;
    });
  };

  // General Information Section
  const addGeneralInformation = () => {
    addSectionHeader('GENERAL INFORMATION');

    const generalFields = [
      { label: 'Scaffolder Owner', value: formData.scaffolderowner },
      { label: 'Inspected By', value: formData.inspectedBy },
      { label: 'Built By', value: formData.builtBy },
      { label: 'User Responsible', value: formData.userResponsible },
      { label: 'Scaffold Class', value: formData.scaffoldClass },
      { label: 'Max Weight per m²', value: formData.totalWeightPerM2 },
      { label: 'Wall Anchors Capacity', value: `${formData.wallAnchorsCapacity || 'N/A'} ${formData.AnchorCapacityUnit || ''}` },
      { label: 'Amount of Wall Anchors', value: formData.amountWallAnkers }
    ];

    generalFields.forEach((field, index) => {
      if (index % 2 === 0) checkPageBreak();
      
      const xPos = (index % 2 === 0) ? margin : pageWidth / 2;
      
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text(`${field.label}:`, xPos, yPosition);
      
      doc.setFont('helvetica', 'normal');
      const textHeight = addWrappedText(field.value || 'N/A', xPos, yPosition + 5, 80, 9);
      
      if (index % 2 === 1) yPosition += Math.max(textHeight + 8, 15);
    });

    if (generalFields.length % 2 === 1) yPosition += 15;

    // User Guide Section
    if (userGuideDetail) {
      checkPageBreak(20);
      
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text('Built According to User Guide:', margin, yPosition);
      
      doc.setFont('helvetica', 'normal');
      doc.text(userGuideDetail.isFile || userGuideDetail.comment ? 'No' : 'Yes', margin + 60, yPosition);
      
      yPosition += 8;

      if (userGuideDetail.comment) {
        doc.setFont('helvetica', 'bold');
        doc.text('Comment:', margin, yPosition);
        yPosition += 5;
        
        doc.setFont('helvetica', 'normal');
        const commentHeight = addWrappedText(userGuideDetail.comment, margin, yPosition, contentWidth - 10, 9);
        yPosition += commentHeight + 5;
      }

      if (userGuideDetail.file) {
        doc.setFont('helvetica', 'bold');
        doc.text('File Attached: Yes', margin, yPosition);
        yPosition += 8;
      }
    }
  };

  // Visual Inspection Section
  const addVisualInspection = () => {
    if (!visual || visual.length === 0) return;

    addSectionHeader('VISUAL INSPECTION');

    // Create table data
    const tableData = visual.map((item, index) => {
      const inspection = item.inspection === 'yes' ? '✓ Yes' : 
                        item.inspection === 'no' ? '✗ No' : 
                        item.inspection === 'na' ? '- N/A' : '- Not Set';
      
      const comment = item.documentComment || 'No comment';
      const hasFile = item.documentFile ? 'Yes' : 'No';
      
      return [
        (index + 1).toString(),
        item.documentList || 'N/A',
        inspection,
        hasFile,
        comment
      ];
    });

    doc.autoTable({
      startY: yPosition,
      head: [['#', 'Inspection Item', 'Status', 'File', 'Comments']],
      body: tableData,
      theme: 'striped',
      styles: {
        fontSize: 8,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [0, 114, 187],
        textColor: 255,
        fontStyle: 'bold'
      },
      columnStyles: {
        0: { cellWidth: 15 },
        1: { cellWidth: 60 },
        2: { cellWidth: 25 },
        3: { cellWidth: 20 },
        4: { cellWidth: 70 }
      },
      alternateRowStyles: {
        fillColor: [248, 249, 250]
      }
    });

    yPosition = doc.lastAutoTable.finalY + 10;
  };

  // Comments Section
  const addCommentsSection = () => {
    addSectionHeader('ADDITIONAL INFORMATION');

    if (formData.followUp) {
      checkPageBreak(20);
      
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text('Specific Follow-up Actions:', margin, yPosition);
      yPosition += 6;
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      const followUpHeight = addWrappedText(formData.followUp, margin, yPosition, contentWidth, 9);
      yPosition += followUpHeight + 10;
    }

    if (formData.comments) {
      checkPageBreak(20);
      
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text('General Comments:', margin, yPosition);
      yPosition += 6;
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      const commentsHeight = addWrappedText(formData.comments, margin, yPosition, contentWidth, 9);
      yPosition += commentsHeight + 10;
    }
  };

  // Signatures Section
  const addSignatures = () => {
    addSectionHeader('SIGNATURES');

    const signatureHeight = 40;
    checkPageBreak(signatureHeight + 20);

    // Customer Signature
    const customerX = margin;
    const inspectorX = pageWidth / 2 + 10;

    // Customer signature box
    doc.setDrawColor(200, 200, 200);
    doc.rect(customerX, yPosition, 80, signatureHeight);
    
    if (approvalForm?.customerSignature) {
      try {
        doc.addImage(approvalForm.customerSignature, 'PNG', customerX + 10, yPosition + 5, 60, 25);
      } catch (error) {
        doc.setFontSize(8);
        doc.text('Signature Present', customerX + 25, yPosition + 20);
      }
    }
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('Customer Signature', customerX + 15, yPosition + signatureHeight + 5);
    
    if (approvalForm?.customerSignatureName) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text(approvalForm.customerSignatureName, customerX + 15, yPosition + signatureHeight + 10);
    }

    // Inspector signature box
    doc.rect(inspectorX, yPosition, 80, signatureHeight);
    
    if (approvalForm?.inspectorSignature) {
      try {
        doc.addImage(approvalForm.inspectorSignature, 'PNG', inspectorX + 10, yPosition + 5, 60, 25);
      } catch (error) {
        doc.setFontSize(8);
        doc.text('Signature Present', inspectorX + 25, yPosition + 20);
      }
    }
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('Inspector Signature *', inspectorX + 15, yPosition + signatureHeight + 5);
    
    if (approvalForm?.inspectorSignatureName) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text(approvalForm.inspectorSignatureName, inspectorX + 15, yPosition + signatureHeight + 10);
    }

    yPosition += signatureHeight + 20;
  };

  // Footer
  const addFooter = () => {
    const pageCount = doc.internal.getNumberOfPages();
    
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      
      // Footer line
      doc.setDrawColor(0, 114, 187);
      doc.line(margin, 285, pageWidth - margin, 285);
      
      // Footer text
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      
      doc.text('Scaffold Approval Form', margin, 290);
      doc.text(`Generated on: ${new Date().toLocaleString()}`, margin, 295);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin - 20, 290);
      
      if (formData.projectNumber) {
        doc.text(`Project: ${formData.projectNumber}`, pageWidth - margin - 50, 295);
      }
    }
  };

  // Generate PDF
  try {
    addHeader();
    addBasicInformation();
    addScaffoldNames();
    addGeneralInformation();
    addVisualInspection();
    addCommentsSection();
    addSignatures();
    addFooter();

    // Save the PDF
    const fileName = `Scaffold_Approval_Form_${formData.scaffoldId || 'New'}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);

    return {
      success: true,
      message: 'PDF generated successfully',
      fileName
    };

  } catch (error) {
    console.error('Error generating PDF:', error);
    return {
      success: false,
      message: 'Error generating PDF: ' + error.message
    };
  }
};

const ApprovalForm = () => {
  const adminId = useSelector((state) => state?.auth?.loggedInUser?._id);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(null);
  const [isProjectOpen, setIsProjectOpen] = useState(true);
  const [newInspectionName, setNewInspectionName] = useState("");
  const [approvalForm, setApprovalForm] = useState([]);
  const userId = useSelector((state) => state?.auth?.loggedInUser?._id);
  const [projectNumber, setProjectNumber] = useState("");
  const [isModalOpenCustomer, setModalOpenCustomer] = useState(false);
  const [isModalOpenInspector, setModalOpenInspector] = useState(false);
  const navigate = useNavigate();
  const [scaffoldData, setScaffoldData] = useState([]);
  const [selectedKeyScaffoldName, setSelectedKeyScaffoldName] = useState("");
  const [selectedValueScaffoldName, setSelectedValueScaffoldName] =
    useState("");
  const [classs, setClasss] = useState("");
  const [backgroundImage, setBackgroundImage] = useState("");
  const [createdApprovalId, setCreatedApprovalId] = useState("");
  const [userGuideCheckbox, setUserGuideCheckBox] = useState(false);
  const [userGuideDetail, setUserGuideDetail] = useState({
    enterCalculation: "",
    file: "",
    comment: "",
    isComment: false,
    isFile: false,
  });
  const [scaffoldStatus, setScaffoldStatus] = useState("inactive");
  const [scaffoldId, setScaffoldId] = useState("");

  const currentLanguage = useSelector(
    (state) => state?.global?.current_language
  );

  const [scaffoldName, setScaffoldName] = useState([]);
  const [isDatePopup, setIsDatePopup] = useState(false);
  const [otherScaffoldType, setOtherScaffoldType] = useState("");
  const inspector = useSelector((state) => state?.auth?.loggedInUser?.name);
  const scffOptions = useSelector((state) => state.scaffolds.scaffoldOptions);
  const result = Object.values(scffOptions).flatMap((item) =>
    Object.keys(item)
      .filter((key) => item[key] !== 0 && key !== "hourlyRate")
      .map((key) => key.replace(/pricePer/i, "").toLowerCase())
  );
  const [PricesTitle, setPricesTile] = useState([]);

  const getProjectDetailById = async () => {
    const response = await getProjectByIdService(projectId);
    const getProjectNumber = response?.data?.project?.projectNumber;
    setProjectNumber(getProjectNumber);
  };

  useEffect(() => {
    if (!projectId) return;
    if (projectId) {
      handleScaffoldForm(projectId);
    }
  }, [projectId]);

  useEffect(() => {
    if (!adminId) return;
    if (adminId) {
      getProjectsDataByUserId(adminId);
    }
  }, [adminId]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  // PDF Generation Function
  const handleDownloadPDF = () => {
    try {
      const currentFormData = {
        workOrderNumber: watch('workOrderNumber'),
        location: watch('location'),
        date: watch('date'),
        dismantledDate: watch('dismantledDate'),
        scaffolderowner: watch('scaffolderowner'),
        inspectedBy: watch('inspectedBy'),
        builtBy: watch('builtBy'),
        userResponsible: watch('userResponsible'),
        scaffoldClass: watch('scaffoldClass'),
        totalWeightPerM2: watch('totalWeightPerM2'),
        wallAnchorsCapacity: watch('wallAnchorsCapacity'),
        AnchorCapacityUnit: watch('AnchorCapacityUnit'),
        amountWallAnkers: watch('amountWallAnkers'),
        followUp: watch('followUp'),
        comments: watch('comments'),
        backgroundImage: backgroundImage,
        scaffoldId: scaffoldId,
        projectNumber: projectNumber
      };

      const result = generateApprovalFormPDF(
        currentFormData,
        approvalForm,
        scaffoldName,
        visual,
        userGuideDetail,
        scaffoldStatus
      );

      if (result.success) {
        toast.success(`PDF downloaded successfully: ${result.fileName}`);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to generate PDF. Please try again.');
    }
  };

  // Preview PDF Function
  const handlePreviewPDF = () => {
    try {
      const currentFormData = {
        workOrderNumber: watch('workOrderNumber') || '[Not filled]',
        location: watch('location') || '[Not filled]',
        date: watch('date') || '[Not filled]',
        dismantledDate: watch('dismantledDate') || '[Not filled]',
        scaffolderowner: watch('scaffolderowner') || '[Not filled]',
        inspectedBy: watch('inspectedBy') || '[Not filled]',
        builtBy: watch('builtBy') || '[Not filled]',
        userResponsible: watch('userResponsible') || '[Not filled]',
        scaffoldClass: watch('scaffoldClass') || '[Not filled]',
        totalWeightPerM2: watch('totalWeightPerM2') || '[Not filled]',
        wallAnchorsCapacity: watch('wallAnchorsCapacity') || '[Not filled]',
        AnchorCapacityUnit: watch('AnchorCapacityUnit') || '[Not filled]',
        amountWallAnkers: watch('amountWallAnkers') || '[Not filled]',
        followUp: watch('followUp') || '[Not filled]',
        comments: watch('comments') || '[Not filled]',
        backgroundImage: backgroundImage,
        scaffoldId: scaffoldId || 'PREVIEW',
        projectNumber: projectNumber || 'PREVIEW'
      };

      generateApprovalFormPDF(
        currentFormData,
        approvalForm,
        scaffoldName,
        visual,
        userGuideDetail,
        scaffoldStatus
      );
      
      toast.info('PDF preview generated with current form data');
    } catch (error) {
      console.error('Error generating preview PDF:', error);
      toast.error('Failed to generate PDF preview');
    }
  };

  const handleAddSignatureCustomer = () => {
    setModalOpenCustomer(true);
  };

  const handleAddSignatureInspector = () => {
    setModalOpenInspector(true);
  };

  const closeModalCustomer = () => {
    setModalOpenCustomer(false);
  };

  const closeModalInspector = () => {
    setModalOpenInspector(false);
  };

  const handleSaveSignatureCustomer = (signatureDataUrl, customerName) => {
    setApprovalForm({
      ...approvalForm,
      customerSignature: signatureDataUrl,
      customerSignatureName: customerName,
    });
    uploadFilesAndImages(customerName, signatureDataUrl);
  };

  const handleSaveSignatureInspector = (signatureDataUrl, inspectorName) => {
    setApprovalForm({
      ...approvalForm,
      inspectorSignature: signatureDataUrl,
      inspectorSignatureName: inspectorName,
    });
    uploadFilesAndImages(inspectorName, signatureDataUrl);
  };

  const { accessLevel: roleOfUser } = useSelector((state) => state?.project);
  const handleDatePopup = () => {
    setIsDatePopup(true);
  };

  const onSubmit = async (formData) => {
    if (scaffoldName?.length < 1) {
      toast.error("Please add at Least one scaffold name");
      return;
    }

    try {
      setLoading(true);
      if (approvalForm?.inspectorSignature) {
        const data = {
          ...formData,
          projectId,
          projectNumber,
          notificationToAdminCreate: roleOfUser === 0 ? false : true,
          userId,
          ...approvalForm,
          sizeScaffold: scaffoldData,
          scaffoldName: scaffoldName,
          visual: visual,
          approvalFormImage: backgroundImage,
          userGuideDetail: { ...userGuideDetail } || null,
          status: scaffoldStatus,
          scaffoldId,
        };

        if (userGuideCheckbox && !userGuideDetail?.file) {
          toast.error("Please upload the file in the User Guide");
          return;
        }
        const response = await approvalFormCreateService(data);
        setCreatedApprovalId(response?.data?._id);
        if (response?.data?.success) {
          toast.success(t("approvalFormCreatedSuccessfully"));
          
          // Offer PDF download after successful save
          setTimeout(() => {
            const shouldDownloadPDF = window.confirm(
              "Form saved successfully! Would you like to download the PDF version?"
            );
            if (shouldDownloadPDF) {
              handleDownloadPDF();
            }
          }, 1000);
          
          setApprovalForm([]);
          reset();
          navigate(`/admin/approval-form`);
        } else {
          toast.error(t("checkAboveInformation"));
        }
      } else {
        toast.error(t("signatureICompulsoryToProcess"));
      }
    } catch (error) {
      toast.error(t("anErrorOccurredWhileProcessingTheApprovalForm"));
    } finally {
      setLoading(false);
    }
  };

  const handleImageUploadBack = (imgUrl, fileTagName) => {
    setBackgroundImage(imgUrl);
  };

  const handleDeleteDocumentBack = () => {
    setBackgroundImage("");
  };

  const uploadFilesAndImages = async (fileName, file) => {
    if (
      fileName.endsWith("Image") ||
      fileName.endsWith("approvalFormImage") ||
      fileName.endsWith("Signature") ||
      file?.includes("data:image/png") ||
      file?.includes("png") ||
      file?.includes("jpg")
    ) {
      const data = {
        userId,
        fileType: "image",
        fileName,
        file,
        projectId,
        isFileFrom: "approval_form",
      };
      // const response = await createFilesService(data);
    } else {
      const data = {
        userId,
        fileType: "file",
        fileName,
        file,
        projectId,
        isFileFrom: "approval_form",
      };
      // const response = await createFilesService(data);
    }
  };

  const handleKeyChangeScaffoldName = (e) => {
    setSelectedKeyScaffoldName(e.target.value);
    if (e.target.value !== "Other") setOtherScaffoldType("");
  };

  const handleValueChangeScaffoldName = (e) => {
    setSelectedValueScaffoldName(e.target.value);
  };

  const handleSaveScaffoldName = () => {
    const scaffoldNameToSave =
      selectedKeyScaffoldName === "Other"
        ? otherScaffoldType
        : selectedKeyScaffoldName;

    // Validation
    if (!selectedValueScaffoldName) {
      toast.error("Please fill the Scaffold Name");
      return;
    }
    if (!selectedKeyScaffoldName) {
      toast.error("Please fill the Scaffold Type");
      return;
    }

    // Update the scaffoldName state
    setScaffoldName((prev) => [
      ...prev,
      { value: selectedValueScaffoldName, key: scaffoldNameToSave },
    ]);

    // Reset input fields
    setSelectedValueScaffoldName("");
    setSelectedKeyScaffoldName("");
  };

  const handleDeleteScaffoldName = (id) => {
    const updatedFormData = [...scaffoldName];
    const filteredData = updatedFormData?.filter((ele, ind) => ind !== id);
    setScaffoldName(filteredData);
  };

  const getWeightForClass = (classs) => {
    switch (classs) {
      case "class1":
        setValue("totalWeightPerM2", "75 kg per m2");
        return "75 kg per m2";
      case "class2":
        setValue("totalWeightPerM2", "150 kg per m2");
        return "150 kg per m2";
      case "class3":
        setValue("totalWeightPerM2", "200 kg per m2");
        return "200 kg per m2";
      case "class4":
        setValue("totalWeightPerM2", "300 kg per m2");
        return "300 kg per m2";
      case "class5":
        setValue("totalWeightPerM2", "450 kg per m2");
        return "450 kg per m2";
      case "class6":
        setValue("totalWeightPerM2", "600 kg per m2");
        return "600 kg per m2";
      default:
        return "";
    }
  };

  function handleInput(event) {
    const inputField = event.target;
    const placeholder = inputField.nextElementSibling;

    if (placeholder) {
      // Check if placeholder exists
      if (inputField.value.trim() !== "") {
        placeholder.style.display = "none";
      } else {
        placeholder.style.display = "inline";
      }
    } else {
      console.warn("No placeholder element found next to the input field.");
    }
  }

  function moveCursorToBeginning(event) {
    const inputField = event.target.previousElementSibling;
    inputField.focus();
    inputField.setSelectionRange(0, 0);
  }

  const placeholders = document.querySelectorAll(".placeholder");

  placeholders.forEach((placeholder) => {
    placeholder.addEventListener("click", moveCursorToBeginning);
  });

  const inputFields = document.querySelectorAll(".with-placeholder");
  inputFields.forEach((inputField) => {
    inputField.addEventListener("input", handleInput);
  });

  const [visual, setVisual] = useState(
    currentLanguage === "en" ? english : norway
  );

  useEffect(() => {
    setVisual(currentLanguage === "en" ? english : norway);
  }, [currentLanguage]);

  const [showInput, setShowInput] = useState(Array(visual?.length).fill(false));
  const [editComment, setEditComment] = useState(
    Array(visual?.length).fill(false)
  );

  const [showComment, setShowComment] = useState(
    Array(visual?.length).fill(false)
  );

  useEffect(() => {
    setShowInput((prevShowInput) => [...prevShowInput, false]);
    setEditComment((prevEditComment) => [...prevEditComment, false]);
    setShowComment((prevShowComment) => [...prevShowComment, false]);
  }, [visual?.length]);

  const handleRadioChange = (id, value) => {
    setVisual(
      visual?.map((item) => {
        if (item.id === id) {
          return {
            ...item,
            inspection: value,
            documentFile: value === "na" ? null : item.documentFile,
            documentComment: value === "na" ? null : item.documentComment,
          };
        }
        return item;
      })
    );
    if (value === "yes" || value === "no") {
      setShowInput((prevInput) =>
        prevInput.map((item, index) => (index === id - 1 ? true : item))
      );
    } else {
      setShowInput((prevInput) =>
        prevInput.map((item, index) => (index === id - 1 ? false : item))
      );
      setEditComment((prevInput) =>
        prevInput.map((item, index) => (index === id - 1 ? false : item))
      );
      setShowComment((prevInput) =>
        prevInput.map((item, index) => (index === id - 1 ? false : item))
      );
    }
  };

  const [loading, setLoading] = useState(false);

  const handleDeleteDocument = (id) => {
    setVisual((prevVisual) => {
      return prevVisual.map((item) => {
        if (item.id === id) {
          return { ...item, documentFile: null };
        }
        return item;
      });
    });
  };

  const handleImageUpload = async (index, file) => {
    const updatedVisual = [...visual];
    const itemToUpdate = updatedVisual.find((item) => item.id === index);
    if (itemToUpdate) {
      itemToUpdate.documentFile = file;
    }
    setVisual(updatedVisual);
    if (file?.includes("data:image/png")) {
      const data = {
        userId,
        fileType: "file",
        file,
        projectId,
        isFileFrom: "approval_form",
        fileName: "approval_signature",
      };
      // await createFilesService(data);
    }
  };

  const handleUploadBackground = async (file) => {
    const data = {
      userId,
      fileType: "image",
      file,
      projectId,
      isFileFrom: "approval_form",
      fileName: "approval_backgroundImage",
    };
    await createFilesService(data);
  };

  const handleAddCommentClick = (index) => {
    const newArr = showComment.map((item, i) => {
      if (index === i) {
        return true;
      } else {
        return item;
      }
    });

    const editArray = editComment.map((item, i) => {
      if (index === i) {
        return false;
      } else {
        return item;
      }
    });
    setShowComment(newArr);
    setEditComment(editArray);
  };

  const handleEditComment = (index, text) => {
    if (text === "add comment") {
      return t("AddComment");
    } else {
      return t("EditComment");
    }
  };

  const handeldeletedit = (index) => {
    const newArr = editComment.map((item, i) => {
      if (index === i) {
        return false;
      } else {
        return item;
      }
    });
    setEditComment(newArr);
  };

  const handleTextChangeEditComment = (index) => {
    const newArr = editComment.map((item, i) => {
      if (index === i) {
        return true;
      } else {
        return item;
      }
    });
    setEditComment(newArr);
  };

  const [comment, setComment] = useState("");

  const handleInputChange = (event) => {
    setComment(event.target.value);
  };

  const handleCommentUpdate = (id) => {
    setVisual(
      visual.map((item) => {
        if (item.id === id) {
          return { ...item, documentComment: comment };
        }
        return item;
      })
    );
  };

  const handleAddVisualInspection = (newValue) => {
    const customData = {
      id: visual?.length + 1,
      documentList: newValue,
      documentFile: null,
      documentComment: null,
      inspection: null,
    };

    if (!!newValue) {
      setVisual((prevState) => [...prevState, customData]);
    } else {
      toast.error("Please fill the Scaffold Inspection");
      return;
    }

    setNewInspectionName("");
  };

  function isValidNumber(value) {
    const regex = /^[0-9]+(\.[0-9]+)?$/;
    return regex.test(value);
  }

  function handleSizeChange(e, scaffoldIndex, unitType, sizeIndex, field) {
    const updatedScaffolds = [...scaffoldName];
    const measurement =
      updatedScaffolds[scaffoldIndex].measurements[unitType][sizeIndex];

    // Ensure the field is properly updated
    measurement[field] = e.target.value;

    setScaffoldName(updatedScaffolds);
  }

  function normalizeUnitType(unitType) {
    // Convert to lowercase and handle special characters
    const normalized = unitType
      .toLowerCase()
      .replace("²", "2")
      .replace("³", "3")
      .replace("m^2", "m2")
      .replace("m^3", "m3");

    // Handle case variations
    switch (normalized) {
      case "m2":
      case "m²":
        return "m2";
      case "m3":
      case "m³" || "m3":
        return "m3";
      case "lm":
      case "LM":
        return "lm";
      case "hm":
      case "HM":
        return "hm";
      default:
        return normalized;
    }
  }

  function renderDynamicInputs(unitType, scaffoldIndex, sizeIndex) {
    const size =
      scaffoldName[scaffoldIndex].measurements?.[unitType]?.[sizeIndex] || {};

    const normalizedUnitType = normalizeUnitType(unitType);

    switch (normalizedUnitType) {
      case "m3":
        return (
          <div className="flex items-center">
            <input
              type="number"
              className="!pl-2 !w-[70px] border !h-[25px] rounded-md shadow-md"
              placeholder="Length"
              value={size.length || ""}
              onChange={(e) =>
                handleSizeChange(
                  e,
                  scaffoldIndex,
                  unitType,
                  sizeIndex,
                  "length"
                )
              }
            />
            <div className="mx-1 font-bold text-xl">*</div>
            <input
              type="number"
              className="!pl-2 !w-[70px] border !h-[25px] rounded-md shadow-md"
              placeholder="Width"
              value={size.width || ""}
              onChange={(e) =>
                handleSizeChange(e, scaffoldIndex, unitType, sizeIndex, "width")
              }
            />
            <div className="mx-1 font-bold text-xl">*</div>
            <input
              type="number"
              className="!pl-2 !w-[70px] border !h-[25px] rounded-md shadow-md"
              placeholder="Height"
              value={size.height || ""}
              onChange={(e) =>
                handleSizeChange(
                  e,
                  scaffoldIndex,
                  unitType,
                  sizeIndex,
                  "height"
                )
              }
            />
          </div>
        );
      case "m2":
        return (
          <div className="flex items-center">
            <input
              type="number"
              className="!pl-2 !w-[70px] border !h-[25px] rounded-md shadow-md"
              placeholder="Length"
              value={size.length || ""}
              onChange={(e) =>
                handleSizeChange(
                  e,
                  scaffoldIndex,
                  unitType,
                  sizeIndex,
                  "length"
                )
              }
            />
            <div className="mx-1 font-bold text-xl">*</div>
            <input
              type="number"
              className="!pl-2 !w-[70px] border !h-[25px] rounded-md shadow-md"
              placeholder="Width"
              value={size.width || ""}
              onChange={(e) =>
                handleSizeChange(e, scaffoldIndex, unitType, sizeIndex, "width")
              }
            />
          </div>
        );
      case "lm":
        return (
          <input
            type="number"
            className="!pl-2 !w-[90px] border !h-[25px] rounded-md shadow-md"
            placeholder="Length"
            value={size.length || ""}
            onChange={(e) =>
              handleSizeChange(e, scaffoldIndex, unitType, sizeIndex, "length")
            }
          />
        );
      case "hm":
        return (
          <input
            type="number"
            className="!pl-2 !w-[90px] border !h-[25px] rounded-md shadow-md"
            placeholder="Height"
            value={size.height || ""}
            onChange={(e) =>
              handleSizeChange(e, scaffoldIndex, unitType, sizeIndex, "height")
            }
          />
        );
      default:
        return (
          <input
            type="number"
            className="!pl-2 !w-[90px] border !h-[25px] rounded-md shadow-md"
            placeholder={unitType.toUpperCase()}
            value={size.value || ""}
            onChange={(e) =>
              handleSizeChange(e, scaffoldIndex, unitType, sizeIndex, "value")
            }
          />
        );
    }
  }

  function addSizeField(scaffoldIndex, unitType) {
    const updatedScaffolds = [...scaffoldName];

    // Normalize the unit type
    const normalizedUnitType = normalizeUnitType(unitType);

    // Initialize the measurements object if not present
    if (!updatedScaffolds[scaffoldIndex].measurements) {
      updatedScaffolds[scaffoldIndex].measurements = {};
    }

    // Initialize the normalizedUnitType array if not present
    if (!updatedScaffolds[scaffoldIndex].measurements[normalizedUnitType]) {
      updatedScaffolds[scaffoldIndex].measurements[normalizedUnitType] = [];
    }

    // Add fields based on normalized unit type
    updatedScaffolds[scaffoldIndex].measurements[normalizedUnitType].push(
      normalizedUnitType === "m3"
        ? { length: "", width: "", height: "" }
        : normalizedUnitType === "m2"
          ? { length: "", width: "" }
          : normalizedUnitType === "lm"
            ? { length: "" }
            : normalizedUnitType === "hm"
              ? { height: "" }
              : { value: "", unit: unitType } // Keep original unit type for display
    );

    setScaffoldName(updatedScaffolds);
  }

  function calculateMeasurement(unitType, size) {
    if (!size || typeof size !== "object") return `0.00 ${unitType}`;

    const normalizedUnitType =
      unitType === "m³"
        ? "m3"
        : unitType === "m²"
          ? "m2"
          : unitType === "LM"
            ? "lm"
            : unitType === "HM"
              ? "hm"
              : unitType;

    const calculations = {
      m3: () =>
        (Number(size?.length) || 0) *
        (Number(size?.width) || 0) *
        (Number(size?.height) || 0),
      m2: () => (Number(size?.length) || 0) * (Number(size?.width) || 0),
      lm: () => Number(size?.length) || 0,
      hm: () => Number(size?.height) || 0,
      Rent: () => Number(size?.value) || Number(size?.Rent) || 0,
      Volume: () => Number(size?.value) || Number(size?.Volume) || 0,
    };

    const result =
      (calculations[normalizedUnitType]?.() ??
        Number(size?.value) ??
        Number(size?.[normalizedUnitType])) ||
      0;

    const formatted = result.toFixed(2); // ✅ round & pad to 2 decimal places

    return `${formatted} ${unitType}`;
  }

  function calculateTotal(measurements) {
    const totals = {};

    if (measurements && typeof measurements === "object") {
      Object.keys(measurements).forEach((unitType) => {
        const normalizedUnitType =
          unitType === "m³"
            ? "m3"
            : unitType === "m²"
              ? "m2"
              : unitType === "LM"
                ? "lm"
                : unitType === "HM"
                  ? "hm"
                  : unitType;

        const total = measurements[unitType]?.reduce((sum, size) => {
          const calculations = {
            m3: () =>
              (Number(size?.length) || 0) *
              (Number(size?.width) || 0) *
              (Number(size?.height) || 0),
            m2: () => (Number(size?.length) || 0) * (Number(size?.width) || 0),
            lm: () => Number(size?.length) || 0,
            hm: () => Number(size?.height) || 0,
            Rent: () => Number(size?.value) || Number(size?.Rent) || 0,
            Volume: () => Number(size?.value) || Number(size?.Volume) || 0,
          };

          const result =
            (calculations[normalizedUnitType]?.() ??
              Number(size?.value) ??
              Number(size?.[normalizedUnitType])) ||
            0;

          return sum + result;
        }, 0);

        totals[unitType] = total.toFixed(2); // ✅ round and pad
      });
    }

    return Object.fromEntries(
      Object.entries(totals).map(([unit, total]) => [unit, `${total} ${unit}`])
    );
  }

  function removeSizeField(scaffoldIndex, unitType, sizeIndex) {
    const updatedScaffolds = [...scaffoldName];
    if (updatedScaffolds[scaffoldIndex]?.measurements?.[unitType]) {
      updatedScaffolds[scaffoldIndex].measurements[unitType].splice(
        sizeIndex,
        1
      );
      setScaffoldName(updatedScaffolds);
    }
  }

  const getApprovalFormList = async (projectId) => {
    const responseData = await getApprovalFormByProjectId(projectId);
    const deriveApproverListNumber =
      responseData?.data?.filter((ele) => !ele?.isDeleted)?.length || 0;

    const formattedNumber = String(deriveApproverListNumber + 1).padStart(
      4,
      "0"
    );
    setScaffoldId(formattedNumber);
  };

  useEffect(() => {
    if (!projectId) return;
    getProjectDetailById();
    getApprovalFormList(projectId);
  }, [getProjectDetailById, projectId]);

  const handleOtherScaffoldType = (value) => {
    setOtherScaffoldType(value);
  };

  const [scaffoldOptions, setScaffoldOptions] = useState({});
  useEffect(() => {
    if (!projectId) return;
    if (projectId) {
      setScaffoldOptions(scffOptions);
    }
  }, [scffOptions, setScaffoldOptions, projectId]);
  useEffect(() => {
    if (!projectId) return;

    const update = (event) => {
      if (event.key === "persist:Salus-Stillas") {
        const updatedState = JSON.parse(event.newValue);
        const updatedScaffoldsOptions = JSON.parse(updatedState?.scaffolds);
        setScaffoldOptions(updatedScaffoldsOptions?.scaffoldOptions || {});
      }
    };

    window.addEventListener("storage", update);
    return () => {
      window.removeEventListener("storage", update);
    };
  }, [projectId]);

  const [rentData, setRentData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [priceFormLoading, setPriceFormLoading] = useState(false);

  const handleScaffoldForm = async (projectId) => {
    setPriceFormLoading(true);
    try {
      const responseData = await getPriceFormByProjectIdService(projectId);

      if (responseData.success) {
        setRentData(responseData?.data?.volume || []);
        if (responseData?.data?.volume?.length > 0) {
          const allKeys = [
            ...new Set(
              responseData?.data?.volume.flatMap((item) =>
                Object.keys(item.prices || {})
              )
            ),
          ];
          const priceKeys = allKeys.filter(
            (key) => key.toLowerCase() !== "rent"
          );
          setPricesTile(priceKeys);

          const dynamicHeaders = [
            "Scaffold Name",
            ...priceKeys.map((key) => `Price (${key})`),
          ];

          setHeaders(dynamicHeaders);
        }
      } else {
        console.error(responseData?.message);
      }
    } catch (error) {
      console.error("Error while fetching scaffold form:", error);
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setPriceFormLoading(false);
    }
  };

  const handleSelect = (e) => {
    setProjectId(e.target.value);
  };

  const handleConfirm = () => {
    if (projectId) {
      setIsProjectOpen(false);
    } else {
      toast.error("Please select a project.");
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const getProjectsDataByUserId = async (id) => {
    if (!id) return;

    setLoading(true);

    try {
      const responseData = await getProjectByUserId(id);
      if (responseData) {
        setProjects(responseData?.data);
      } else {
        setProjects([]);
      }
    } catch (err) {
      console.error("Error fetching projects:", err);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const [searchTerm, setSearchTerm] = useState("");
  const filteredProjects = projects.filter((project) => {
    const searchLower = searchTerm.toLowerCase();
    const nameMatch = project.projectName.toLowerCase().includes(searchLower);
    const numberMatch = project.projectNumber.toString().includes(searchLower);
    return nameMatch || numberMatch;
  });

  useEffect(() => {
    if (!projectId) return;
    if (searchTerm && !filteredProjects.some((p) => p._id === projectId)) {
      setProjectId("");
    }
  }, [searchTerm, filteredProjects, projectId]);

  return (
    <MainLayout type="New Approval Form">
      <form
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
          }
        }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="sm:pb-[50px] pb-6 border-b-[#cccccc] border-b">
          <div className="custom-container flex flex-col md:flex-row gap-[20px] justify-between items-center">
            <p className="title-text">{t("scaffoldForm")}</p>
            <div className="relative">
              <div className="flex justify-between items-center">
                <div className="flex justify-center items-center gap-[1rem] flex-wrap">
                  <div
                    className="flex justify-between rounded-[5px] items-center gap-[30px] px-[10px] py-[11px] bg-[white]"
                    style={{ border: "1px solid #ccc" }}
                  >
                    <p className="project-number leading-0">
                      {t("scaffold_id")}:
                    </p>
                    <p className="medium-title leading-0">{scaffoldId}</p>
                    <p className="project-number leading-0">
                      {t("projectNumber")}
                    </p>
                    <p className="medium-title leading-0">{projectNumber}</p>
                  </div>
                  {backgroundImage ? (
                    <ImageUpload
                      onImageUpload={handleImageUploadBack}
                      documentFile={"approvalFormImage"}
                      handleDeleteDocument={handleDeleteDocumentBack}
                      status={true}
                    />
                  ) : (
                    <ImageUpload
                      onImageUpload={handleImageUploadBack}
                      documentFile={"approvalFormImage"}
                      status={false}
                      onImageUpload1={(file) => handleUploadBackground(file)}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-between sm:gap-[20px] gap-4 flex-wrap w-full items-end">
          <div className="w-full lg:w-[calc(50%-10px)] border rounded-md p-[10px]">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("ScaffoldName")}
            </label>
            <div className="flex flex-wrap gap-2 mb-[10px]">
              {scaffoldName?.length > 0 &&
                scaffoldName?.map((element, index) => (
                  <div key={index} className="p-2 font-semibold rounded flex items-center gap-3 text-[12px] bg-[#0072BB1A]">
                    {element?.value} - ({element?.key})
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        handleDeleteScaffoldName(index);
                      }}
                    >
                      <img
                        className="text-[#0072BB]"
                        src={CloseIcon}
                        alt="edit_document"
                      />
                    </button>
                  </div>
                ))}
            </div>

            <div className="flex gap-3 sm:flex-row flex-col">
              <input
                type="text"
                className="w-32 p-2 border rounded-md shadow-md input-without-icon"
                onChange={(e) => handleValueChangeScaffoldName(e)}
                placeholder={t("scaffold_name")}
                value={selectedValueScaffoldName}
              />
              <select
                className="p-2 border rounded-md shadow-md input-without-icon"
                onChange={(e) => handleKeyChangeScaffoldName(e)}
                value={selectedKeyScaffoldName}
              >
                <option key={"1234"} disabled value={""}>
                  {"Select Scaffold"}
                </option>

                {rentData?.map((scaffold) => (
                  <option key={scaffold} value={scaffold?.scaffoldName}>
                    {scaffold?.scaffoldName}
                  </option>
                ))}
              </select>

              {/* Conditionally render the input when "Other" is selected */}
              {selectedKeyScaffoldName === "Other" && (
                <input
                  type="text"
                  className="w-32 p-2 border rounded-md shadow-md"
                  placeholder={t("Enter other type")}
                  onChange={(e) =>
                    handleOtherScaffoldType(e.target.value)
                  }
                  value={otherScaffoldType}
                />
              )}

              <button
                className="bg-[#0072BB] button-text px-[18px] py-[10px] text-white rounded-[5px]"
                onClick={(e) => {
                  e.preventDefault();
                  handleSaveScaffoldName();
                }}
              >
                {t("Save")}
              </button>
            </div>
          </div>

          <div className="w-full lg:w-[calc(50%-10px)]">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("WorkOrderNumber")}
            </label>
            <input
              className="input-without-icon w-full p-2 border rounded-md"
              type="text"
              required
              placeholder={t("WorkOrderNumber")}
              {...register("workOrderNumber", {
                required: true,
              })}
            />
          </div>

          <div className="w-full lg:w-[calc(50%-10px)]">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("specificLocation")}
            </label>
            <input
              className="input-without-icon w-full p-2 border rounded-md"
              type="text"
              required
              placeholder={t("specificLocation")}
              {...register("location", { required: true })}
            />
          </div>

          <div className="relative w-full lg:w-[calc(50%-10px)] flex gap-[10px]">
            <div className="relative w-full lg:w-[calc(50%-10px)]">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("buildDay")}
              </label>
              <input
                className="input-without-icon w-full p-2 border rounded-md"
                type="date"
                required
                {...register("date", { required: true })}
              />
            </div>
            <div className="relative w-full lg:w-[calc(50%-10px)]">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("dismantleDay")}
              </label>
              <input
                className="input-without-icon w-full p-2 border rounded-md"
                type="date"
                required
                {...register("dismantledDate", { required: true })}
              />
            </div>
          </div>
        </div>

        <div className="sm:pb-[50px] pb-6 border-b-[#cccccc]">
          <div className="custom-container">
            <div className="flex flex-col sm:gap-[30px] gap-4 sm:mt-[60px] mt-6">
              <div className="flex justify-between items-center">
                <p className="medium-title">{t("generalInformation")}</p>
              </div>
              <div className="flex justify-between items-start sm:gap-[20px] gap-4 flex-wrap w-full">

                <div className="w-full lg:w-[calc(50%-10px)]">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("scaffolderOwner")}
                  </label>
                  <input
                    className="input-without-icon w-full p-2 border rounded-md"
                    type="text"
                    placeholder={t("scaffolderOwner")}
                    {...register("scaffolderowner", { required: true })}
                  />
                  {errors?.scaffolderowner && (
                    <span style={styles}>
                      {errors?.scaffolderowner?.message}
                    </span>
                  )}
                </div>

                <div className="w-full lg:w-[calc(50%-10px)]">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("inspectedBy")}
                  </label>
                  <input
                    className="input-without-icon w-full p-2 border rounded-md"
                    type="text"
                    placeholder={t("inspectedBy")}
                    {...register("inspectedBy", { required: true })}
                  />
                  {errors?.inspectedBy && (
                    <span style={styles}>{errors?.inspectedBy?.message}</span>
                  )}
                </div>

                <div className="w-full lg:w-[calc(50%-10px)]">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("builtBy")}
                  </label>
                  <input
                    className="input-without-icon w-full p-2 border rounded-md"
                    type="text"
                    placeholder={t("builtBy")}
                    {...register("builtBy", { required: true })}
                  />
                  {errors?.builtBy && (
                    <span style={styles}>{errors?.builtBy?.message}</span>
                  )}
                </div>

                <div className="w-full lg:w-[calc(50%-10px)]">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("userResponsible")}
                  </label>
                  <input
                    className="input-without-icon w-full p-2 border rounded-md"
                    type="text"
                    placeholder={t("userResponsible")}
                    {...register("userResponsible", { required: true })}
                  />
                  {errors?.userResponsible && (
                    <span style={styles}>
                      {errors?.userResponsible?.message}
                    </span>
                  )}
                </div>

                <div className="w-full lg:w-[calc(50%-10px)]">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("scaffoldClass")}
                  </label>
                  <select
                    {...register("scaffoldClass")}
                    onChange={(e) => setClasss(e.target.value)}
                    className="bg-white border border-gray-300 text-sm rounded-lg block w-full p-[1rem] outline-none"
                  >
                    <option value="">{t("selectScaffoldClass")}</option>
                    <option value="class1">{t("Class1")}</option>
                    <option value="class2">{t("Class2")}</option>
                    <option value="class3">{t("Class3")}</option>
                    <option value="class4">{t("Class4")}</option>
                    <option value="class5">{t("Class5")}</option>
                    <option value="class6">{t("Class6")}</option>
                  </select>
                  {errors?.scaffoldClass && (
                    <span style={styles}>{errors?.scaffoldClass?.message}</span>
                  )}
                </div>

                <div className="w-full lg:w-[calc(50%-10px)]">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("maximumWeightPerm2Inkilograms")}
                  </label>
                  <input
                    className="input-without-icon w-full p-2 border rounded-md"
                    type="text"
                    value={getWeightForClass(classs)}
                    placeholder={t("maximumWeightPerm2Inkilograms")}
                    {...register("totalWeightPerM2", { required: true })}
                  />
                  {errors?.totalWeightPerM2 && (
                    <span style={styles}>
                      {errors?.totalWeightPerM2?.message}
                    </span>
                  )}
                </div>

                <div className="w-full lg:w-[calc(50%-10px)]">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("WallAnchorsCapacity")}
                  </label>
                  <div className="flex">
                    <input
                      className="input-without-icon flex-1 p-2 border"
                      type="text"
                      placeholder={t("WallAnchorsCapacity")}
                      {...register("wallAnchorsCapacity", { required: true })}
                      style={{ borderRadius: "5px 0px 0px 5px" }}
                    />
                    <div className="w-32">
                      <select
                        {...register("AnchorCapacityUnit")}
                        className="border border-gray-300 text-sm rounded-r-lg block w-full h-[50px] p-[1rem] outline-none"
                      >
                        <option value="">{t("AnchorCapacityUnit")}</option>
                        <option value="KN">KN</option>
                        <option value="KG">KG</option>
                      </select>
                    </div>
                  </div>
                  {errors?.wallAnchorsCapacity && (
                    <span style={styles}>
                      {errors?.wallAnchorsCapacity?.message}
                    </span>
                  )}
                  {errors?.AnchorCapacityUnit && (
                    <span style={styles}>
                      {errors?.AnchorCapacityUnit?.message}
                    </span>
                  )}
                </div>

                <div className="w-full lg:w-[calc(50%-10px)]">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("amountOfWallAnkers")}
                  </label>
                  <input
                    className="input-without-icon w-full p-2 border rounded-md"
                    type="text"
                    placeholder={t("amountOfWallAnkers")}
                    {...register("amountWallAnkers", { required: true })}
                  />
                  {errors?.amountWallAnkers && (
                    <span style={styles}>
                      {errors?.amountWallAnkers?.message}
                    </span>
                  )}
                </div>

                <div className="w-full lg:w-[calc(50%-10px)] rounded-md p-[10px]">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("buildAccording")}
                  </label>
                  <div className="flex w-full">
                    <div className="flex justify-between lg:justify-end lg:flex-row-reverse flex-row items-center w-full gap-[10px]">
                      <p className="project-number">{t("Built according to user guide?")}</p>
                    </div>
                    <div className="flex justify-center items-center gap-[20px] lg:gap-[64px]">
                      <div className="flex sm:gap-5 gap-2">
                        <input
                          type="radio"
                          name="buildAccording"
                          value="yes"
                          checked={!userGuideCheckbox}
                          onClick={() => {
                            setUserGuideCheckBox(false);
                            setUserGuideDetail({
                              enterCalculation: "",
                              file: "",
                              comment: "",
                              isComment: false,
                              isFile: false,
                            });
                          }}
                        />
                        {t("Yes")}
                      </div>
                      <div className="flex sm:gap-5 gap-2">
                        <input
                          type="radio"
                          name="buildAccording"
                          value="no"
                          checked={userGuideCheckbox}
                          onClick={() => {
                            setUserGuideCheckBox(true);
                          }}
                        />
                        {t("No")}
                      </div>
                    </div>
                  </div>
                  {userGuideCheckbox && (
                    <div className="flex gap-5 mt-4 text-sm">
                      <div
                        onClick={() =>
                          setUserGuideDetail({
                            ...userGuideDetail,
                            isComment: false,
                            isFile: true,
                          })
                        }
                        className={`flex flex-row gap-[10px] w-auto items-center p-0.5 ${userGuideDetail.file ? "" : `border-[1px]`
                          } border-[#CCCCCC] px-[10px] rounded-[5px] ${userGuideDetail.file ? `bg-[#0072BB1A]` : ""
                          }`}
                      >
                        <LiaFileUploadSolid
                          backgroundColor="blue"
                          color={userGuideDetail?.file ? "#0072BB" : "black"}
                          size={20}
                        />
                        <ImageUpload
                          userGuideDetail={userGuideDetail}
                          setUserGuideDetail={setUserGuideDetail}
                          isUserGuideDetail={true}
                        />
                      </div>

                      <div>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            setUserGuideDetail({
                              ...userGuideDetail,
                              isComment: !userGuideDetail.isComment,
                            });
                          }}
                          className={`${userGuideDetail?.comment
                            ? "text-[#0072BB] bg-[#e5f1f8] !border-[#e5f1f8]"
                            : "text-black "
                            } flex gap-3 text-sm p-0.5 border rounded border-gray-300 px-2 cursor-pointer`}
                        >
                          {userGuideDetail.comment ? (
                            <>
                              <MdOutlineEditNote color="#0072BB" size={20} />
                            </>
                          ) : (
                            <MdOutlineEditNote color="#000000" size={20} />
                          )}
                          Add Comment
                        </button>
                      </div>
                    </div>
                  )}
                  {userGuideDetail?.isComment && (
                    <div className="mt-3">
                      <textarea
                        id="message"
                        rows="4"
                        className="rounded w-full border border-gray-300 p-3"
                        onChange={(e) => {
                          setUserGuideDetail({
                            ...userGuideDetail,
                            comment: e.target.value,
                          });
                        }}
                        placeholder={t("Write your thoughts here...")}
                      >
                        {userGuideDetail?.comment}
                      </textarea>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {scaffoldName?.length > 0 && (
          <div className="custom-container sm:pb-[50px] pb-6 border-b-[#cccccc] border-b">
            <div className="my-5">
              <p className="text-xl font-bold">Size of Scaffold:</p>
            </div>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th></th>
                    <th>Name</th>
                    {/* <th>Unit</th> */}
                    <th>Size</th>
                    <th>Calculation</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {scaffoldName?.map((element, index) => (
                    <tr key={index}>
                      <th>{index + 1}</th>
                      <td>
                        {element?.value} - ({element?.key})
                      </td>
                      <td>
                        {PricesTitle?.map((unitType) => {
                          const normalizedType = normalizeUnitType(unitType);
                          return element?.measurements?.[normalizedType]?.map(
                            (size, sizeIndex) => (
                              <div
                                key={`${normalizedType}-${sizeIndex}`}
                                className="flex items-center"
                              >
                                {renderDynamicInputs(
                                  normalizedType,
                                  index,
                                  sizeIndex
                                )}
                                <button
                                  type="button"
                                  className="ml-2 text-red-500"
                                  onClick={() =>
                                    removeSizeField(
                                      index,
                                      normalizedType,
                                      sizeIndex
                                    )
                                  }
                                >
                                  <MdDeleteOutline className="text-[20px]" />
                                </button>
                              </div>
                            )
                          );
                        })}
                        <select
                          className="mt-2 text-[#0072BB] border px-2 py-2 mr-1 rounded-xl border-[#0072BB]"
                          onChange={(e) => addSizeField(index, e.target.value)}
                        >
                          <option value="" disabled selected>
                            Select Size
                          </option>
                          {rentData?.map((unitType) => {
                            // Only proceed if scaffoldName matches and is not rent (case-insensitive)
                            if (
                              unitType.scaffoldName === element?.key &&
                              unitType.scaffoldName.toLowerCase() !== "rent"
                            ) {
                              return Object.keys(unitType?.prices)
                                .filter(
                                  (key) =>
                                    key.toLowerCase() !== "volume" &&
                                    key.toLowerCase() !== "rent"
                                ) // Filter out volume and rent from prices
                                .map((key) => (
                                  <option key={key} value={key}>
                                    Add {key} Size
                                  </option>
                                ));
                            }
                            return null;
                          })}
                        </select>
                      </td>
                      <td>
                        {PricesTitle?.map((unitType) => {
                          const normalizedType = normalizeUnitType(unitType);
                          return element?.measurements?.[normalizedType]?.map(
                            (size, sizeIndex) => (
                              <div
                                key={`${normalizedType}-${sizeIndex}`}
                                className="border p-1 rounded-xl flex flex-col mb-2 border-[#0072BB]"
                              >
                                {calculateMeasurement(normalizedType, size)}
                              </div>
                            )
                          );
                        })}
                      </td>
                      <td>
                        {PricesTitle?.filter((unitType) => {
                          const normalizedType = normalizeUnitType(unitType);
                          return element?.measurements?.[normalizedType];
                        }).map((unitType) => {
                          const normalizedType = normalizeUnitType(unitType);
                          const totalValue = calculateTotal(
                            element?.measurements
                          )?.[normalizedType];

                          return (
                            <div
                              key={normalizedType}
                              className="border p-1 rounded-xl flex flex-col mb-2 border-[#0072BB]"
                            >
                              {totalValue || `0 ${unitType}`}
                            </div>
                          );
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="sm:pb-[50px] pb-6 border-b-[#cccccc] border-b">
          <div className="custom-container">
            <div className="flex flex-col sm:gap-[30px] gap-4 sm:mt-[60px] mt-6">
              <div className="flex justify-between items-center">
                <p className="medium-title">{t("visualInspection")}</p>
              </div>

              <div className="grid grid-cols-1 2xl:grid-cols-2 gap-6 w-full">
                {visual.map((item, index) => (
                  <div
                    key={index}
                    className="w-full bg-white border border-gray-200 rounded-lg p-4 sm:p-6 shadow-sm"
                  >
                    {/* Main content row */}
                    <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
                      {/* Document name */}
                      <div className="flex-shrink-0">
                        <p className="project-number font-medium text-gray-900">
                          {item?.documentList}
                        </p>
                      </div>

                      {/* Radio buttons */}
                      <div className="flex flex-wrap gap-4 sm:gap-8 lg:gap-[64px]">
                        <div className="flex items-center gap-2">
                          <input
                            type="radio"
                            name={`option-${index}`}
                            value="yes"
                            checked={item.inspection === "yes" || ""}
                            onChange={() =>
                              handleRadioChange(
                                item.id,
                                "yes",
                                item?.inspection
                              )
                            }
                            className="w-4 h-4 text-blue-600"
                          />
                          <label className="text-sm font-medium text-gray-700">
                            {t("Yes")}
                          </label>
                        </div>

                        <div className="flex items-center gap-2">
                          <input
                            type="radio"
                            name={`option-${index}`}
                            value="no"
                            checked={item.inspection === "no"}
                            onChange={() =>
                              handleRadioChange(item.id, "no", item?.inspection)
                            }
                            className="w-4 h-4 text-blue-600"
                          />
                          <label className="text-sm font-medium text-gray-700">
                            {t("No")}
                          </label>
                        </div>

                        <div className="flex items-center gap-2">
                          <input
                            type="radio"
                            name={`option-${index}`}
                            value="NA"
                            checked={item.inspection === "na"}
                            onChange={() =>
                              handleRadioChange(item.id, "na", item?.inspection)
                            }
                            className="w-4 h-4 text-blue-600"
                          />
                          <label className="text-sm font-medium text-gray-700">
                            N/A
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Action buttons section */}
                    <div
                      className={`mt-4 pt-4 border-t border-gray-100 ${showInput[index] ? "" : "hidden"
                        }`}
                    >
                      <div className="flex flex-col sm:flex-row gap-3">
                        {/* File upload section */}
                        <div
                          className={`flex flex-row gap-[10px] items-center px-[10px] py-2 rounded-[5px] ${item.documentFile
                            ? "bg-[#0072BB1A]"
                            : "border-[1px] border-[#CCCCCC]"
                            }`}
                        >
                          <LiaFileUploadSolid
                            backgroundColor="blue"
                            color={item.documentFile ? "#0072BB" : "black"}
                            size={20}
                          />
                          {item.documentFile ? (
                            <ImageUpload
                              editedImage={item.documentFile}
                              onImageUpload1={(file) =>
                                handleImageUpload(item?.id, file)
                              }
                              status={true}
                              documentFile={item?.documentFile}
                              index={item?.id}
                              handleDeleteDocument={() =>
                                handleDeleteDocument(item?.id)
                              }
                            />
                          ) : (
                            <ImageUpload
                              onImageUpload1={(file) =>
                                handleImageUpload(item?.id, file)
                              }
                              documentFile={item?.documentFile}
                              index={item?.id}
                            />
                          )}
                        </div>

                        {/* Comment section */}
                        <div
                          className={`flex flex-row gap-[10px] items-center px-[10px] py-2 rounded-[5px] ${editComment?.[index]
                            ? "bg-[#0072BB1A]"
                            : "border-[1px] border-[#CCCCCC]"
                            }`}
                        >
                          <MdOutlineEditNote
                            color={editComment?.[index] ? "#0072BB" : "#000000"}
                            size={20}
                          />
                          <button
                            className={`text-sm ${editComment?.[index]
                              ? "text-[#0072BB]"
                              : "text-black"
                              }`}
                            onClick={(event) => {
                              event.preventDefault();
                              handleAddCommentClick(index);
                            }}
                          >
                            {editComment?.[index]
                              ? handleEditComment(index, "edit comment")
                              : handleEditComment(index, "add comment")}
                          </button>
                          {editComment[index] && (
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                handeldeletedit(index);
                              }}
                              className="ml-2"
                            >
                              <img
                                className="text-[#0072BB] w-4 h-4"
                                src={CloseIcon}
                                alt="edit_document"
                              />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Comment textarea section */}
                    {showComment[index] && !editComment[index] && (
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <div className="flex flex-col gap-4">
                          <label
                            htmlFor="message"
                            className="block text-sm font-medium text-black"
                          >
                            {t("Yourmessage")}
                          </label>
                          <textarea
                            id="message"
                            rows="4"
                            className="w-full rounded border border-gray-300 p-3 text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            onChange={handleInputChange}
                            placeholder={t("Writeyourthoughtshere...")}
                            defaultValue={
                              visual?.filter((el) => el.id === item?.id)[0]
                                ?.documentComment
                            }
                          />
                          <div className="flex justify-start">
                            <button
                              onClick={(event) => {
                                event.preventDefault();
                                handleCommentUpdate(item.id);
                                handleTextChangeEditComment(index);
                              }}
                              className="bg-[#0072BB] text-white px-4 py-2 rounded-[5px] text-sm font-medium hover:bg-blue-700 transition-colors"
                            >
                              {t("Save")}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {/* Add new inspection section */}
                <div className="w-full bg-gray-50 border border-gray-200 rounded-lg p-4 sm:p-6 2xl:col-span-2">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input
                      placeholder={t("Entervisualinspection")}
                      className="flex-1 border rounded-md px-3 py-2 text-sm text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      onChange={(e) => setNewInspectionName(e.target.value)}
                      value={newInspectionName || ""}
                    />
                    <button
                      className="bg-[#0072BB] px-4 py-2 text-white rounded-[5px] text-sm font-medium hover:bg-blue-700 transition-colors whitespace-nowrap"
                      onClick={(event) => {
                        event.preventDefault();
                        handleAddVisualInspection(newInspectionName);
                        setNewInspectionName("");
                      }}
                    >
                      {t("Add")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="custom-container">
          <div className="flex flex-col sm:gap-[30px] gap-4 sm:mt-[60px] mt-6">
            <div className="flex justify-between items-end">
              <p className="w-full lg:w-[calc(50%-10px)] medium-title">
                {t("areThereAnySpecific")}
              </p>
              <p className="w-full lg:w-[calc(50%-10px)] medium-title hidden lg:block">
                {t("comments")}
              </p>
            </div>
            <div className="flex justify-between sm:gap-[20px] sm:gap-4 flex-wrap w-full">
              <div className="w-full lg:w-[calc(50%-10px)]">
                <textarea
                  className="w-full p-[20px] mb-3 border rounded-[5px]"
                  name="demo1"
                  id="demo1"
                  rows="3"
                  placeholder={t("writeHere")}
                  {...register("followUp", { required: true })}
                ></textarea>
              </div>
              <p className="w-full lg:w-[calc(50%-10px)] medium-title block lg:hidden mb-2">
                {t("comments")}
              </p>
              <div className="w-full lg:w-[calc(50%-10px)]">
                <textarea
                  className="w-full p-[20px] border rounded-[5px]"
                  name="demo1"
                  id="demo1"
                  rows="3"
                  placeholder={t("writeHere")}
                  {...register("comments", { required: true })}
                ></textarea>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:gap-[30px] gap-4 sm:mt-[60px] mt-6">
            <div className="flex justify-between items-center">
              <p className="medium-title">{t("signature")}</p>
            </div>
            <div className="flex flex-col lg:flex-row sm:gap-[50px] gap-6 justify-between items-center lg:pl-[100px]">
              <div className="flex flex-col sm:gap-[20px] gap-3 w-full lg:w-[380px] items-center">
                {approvalForm?.customerSignature ? (
                  <>
                    <img
                      className="m-auto"
                      width={169}
                      src={approvalForm.customerSignature}
                      alt="Signature"
                    />
                    <p className="text-black  text-[cap] font-Montserrat font-bold text-base leading-7">
                      {_.startCase(
                        _.toLower(approvalForm?.customerSignatureName)
                      )}
                    </p>
                    <button
                      onClick={() =>
                        setApprovalForm({
                          ...approvalForm,
                          customerSignature: null,
                        })
                      }
                    >
                      {t("clearSignature")}
                    </button>
                  </>
                ) : (
                  <div
                    className="flex flex-col cursor-pointer justify-center  gap-[20px] items-center"
                    onClick={(e) => {
                      e.preventDefault();
                      handleAddSignatureCustomer();
                    }}
                  >
                    <img src="/addShape.svg" alt="sign-add" />
                    <button>{t("AddSignature")}</button>
                  </div>
                )}
                <div className="w-full border"></div>
                <p>{t("signatureOfTheCustomer")}</p>
              </div>
              <div className="flex flex-col sm:gap-[20px] gap-3 w-full lg:w-[380px] items-center">
                <div className="w-full flex gap-[20px] flex-col items-center">
                  {approvalForm?.inspectorSignature ? (
                    <>
                      <img
                        className="m-auto"
                        width={169}
                        src={approvalForm?.inspectorSignature}
                        alt="Signature"
                      />
                      <p className="text-black  text-[cap] font-Montserrat font-bold text-base leading-7">
                        {_.startCase(
                          _.toLower(approvalForm?.inspectorSignatureName)
                        )}
                      </p>
                      <button
                        onClick={() =>
                          setApprovalForm({
                            ...approvalForm,
                            inspectorSignature: null,
                            inspectorSignatureName: inspector,
                          })
                        }
                      >
                        {t("clearSignature")}
                      </button>
                    </>
                  ) : (
                    <div
                      className="flex flex-col cursor-pointer justify-center  gap-[20px] items-center"
                      onClick={(e) => {
                        e.preventDefault();
                        handleAddSignatureInspector();
                      }}
                    >
                      <img src="/addShape.svg" alt="sign-add" />
                      <button>{t("AddSignature")}</button>
                    </div>
                  )}
                </div>
                <div className="w-full border"></div>
                <div>
                  {t("signatureOfTheInspector")}
                  <span style={{ color: "red", fontSize: "1.5em" }}>*</span>
                </div>
              </div>
            </div>
            <SignatureModal
              isOpen={isModalOpenCustomer}
              onClose={closeModalCustomer}
              onSave={handleSaveSignatureCustomer}
            />
            <SignatureModal
              isOpen={isModalOpenInspector}
              onClose={closeModalInspector}
              onSave={handleSaveSignatureInspector}
              inspector={inspector}
            />
          </div>
        </div>

        <div className="text-center md:mt-[100px] sm:mt-12 mt-6">
          <button
            type="submit"
            // onClick={handleDatePopup}
            className="button-text bg-[#0072BB] text-[white] px-[20px] py-[10px] rounded-[5px]"
          >
            {loading ? (
              <div className="flex justify-center items-center gap-2">
                Loading
                <span className="loading loading-bars loading-sm"></span>
              </div>
            ) : (
              t("Save")
            )}
          </button>
        </div>
      </form>

      {isProjectOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 transition-opacity duration-300 ease-in-out">
          <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md mx-4 transform transition-all duration-300 ease-out scale-100 opacity-100">
            <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
              Select Project for New Approval Form
            </h2>

            {/* Search input */}
            <div className="relative mb-4">
              <input
                type="text"
                placeholder="Search by project name or number..."
                className="w-full bg-gray-50 border border-gray-300 rounded-xl p-3 pl-10 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-500">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              {searchTerm && (
                <button
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                  onClick={() => setSearchTerm("")}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              )}
            </div>

            {projects.length === 0 ? (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-center">
                <div className="flex items-center justify-center mb-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-2"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="font-semibold">No Projects Available</span>
                </div>
                <p className="text-sm">
                  Please create a new project to continue.
                </p>
              </div>
            ) : filteredProjects.length === 0 ? (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-xl text-yellow-700 text-center">
                <div className="flex items-center justify-center mb-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-2"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="font-semibold">No Matching Projects</span>
                </div>
                <p className="text-sm">
                  No projects match your search. Try different keywords.
                </p>
              </div>
            ) : (
              <div className="max-h-60 overflow-y-auto mb-6 bg-gray-50 border border-gray-300 rounded-xl">
                {filteredProjects.map((project) => (
                  <div
                    key={project._id}
                    className={`p-3 border-b border-gray-200 last:border-b-0 cursor-pointer hover:bg-blue-50 transition-colors duration-200 ${projectId === project._id ? "bg-blue-100" : ""
                      }`}
                    onClick={() => setProjectId(project._id)}
                  >
                    <div className="flex items-center">
                      <div className="flex-1">
                        <div className="font-medium text-gray-800">
                          {project.projectName}
                        </div>
                        <div className="text-sm text-gray-600">
                          #{project.projectNumber}
                        </div>
                      </div>
                      {projectId === project._id && (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 text-blue-600"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center space-x-2 my-6">
              <div className="flex-1 border-t border-gray-200"></div>
              <span className="text-xs font-medium text-gray-500 px-3 bg-white">
                OR
              </span>
              <div className="flex-1 border-t border-gray-200"></div>
            </div>

            <Link to="/admin/project/create-new-project" className="block mb-6">
              <button className="w-full flex items-center justify-center gap-2 py-3 px-4 border-2 border-dashed border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 hover:border-blue-400 hover:text-blue-600 transition-all duration-200 font-medium">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                    clipRule="evenodd"
                  />
                </svg>
                Create New Project
              </button>
            </Link>

            <div className="grid grid-cols-2 gap-4 mt-6">
              <button
                onClick={handleGoBack}
                className="w-full bg-white border border-gray-300 text-gray-700 font-medium py-3 px-4 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300"
              >
                Back
              </button>
              <button
                onClick={handleConfirm}
                className={`w-full font-medium py-3 px-4 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 ${!projectId && filteredProjects.length > 0
                  ? "bg-blue-400 text-white cursor-not-allowed opacity-70"
                  : "bg-blue-600 text-white hover:bg-blue-700 shadow-md hover:shadow-lg"
                  }`}
                disabled={!projectId && filteredProjects.length > 0}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default ApprovalForm;
